import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Send, User, MapPin, Sparkles, Heart, Baby } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PrivateChat = () => {
  const { connectionId } = useParams();
  const { user } = useAuth();
  const [connection, setConnection] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  const otherUser = connection?.connected_user;

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'ttc': return <Sparkles className="w-5 h-5" />;
      case 'pregnant': return <Heart className="w-5 h-5" />;
      case 'postpartum': return <Baby className="w-5 h-5" />;
      default: return <Heart className="w-5 h-5" />;
    }
  };

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case 'ttc': return 'Trying to Conceive';
      case 'pregnant': return 'Pregnant';
      case 'postpartum': return 'Postpartum';
      default: return phase;
    }
  };

  useEffect(() => {
    fetchChatData();
    
    // Poll for new messages every 3 seconds
    pollingRef.current = setInterval(fetchMessages, 3000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [connectionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChatData = async () => {
    setLoading(true);
    try {
      const [connRes, msgsRes] = await Promise.all([
        axios.get(`${API}/connections/${connectionId}`, { withCredentials: true }),
        axios.get(`${API}/chat/${connectionId}/messages`, { withCredentials: true })
      ]);
      setConnection(connRes.data);
      setMessages(msgsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load chat");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/${connectionId}/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error("Fetch messages error:", error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/chat/${connectionId}/messages`, {
        content: newMessage
      }, { withCredentials: true });
      
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      console.error("Send message error:", error?.response?.data || error);
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403) {
        toast.error("Unable to send message. This connection may no longer be active.");
      } else if (error?.response?.status === 401) {
        toast.error("Session expired. Please refresh the page.");
      } else {
        toast.error(detail || "Failed to send message. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <div className="px-6 pt-6 max-w-2xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-16 bg-sage/10 rounded-xl" />
            <div className="h-[400px] bg-sage/10 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!connection) {
    return (
      <div className="min-h-screen bg-cream">
        <div className="px-6 pt-6 text-center">
          <p className="text-muted-foreground">Chat not found</p>
          <Link to="/discover">
            <Button className="mt-4">Back to Discover</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Chat Header */}
      <header className="px-6 py-4 border-b border-sage/10 bg-white sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Link to="/discover" className="text-muted-foreground hover:text-sage-dark">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          
          <button 
            onClick={() => setProfileOpen(true)}
            className="flex items-center gap-3 flex-1 hover:bg-sage/5 rounded-xl p-1 -m-1 transition-colors"
          >
            <Avatar className="w-12 h-12 border-2 border-sage/20">
              <AvatarImage src={otherUser?.picture} />
              <AvatarFallback className="bg-sage/20 text-sage-dark">
                {otherUser?.name?.[0]}
              </AvatarFallback>
            </Avatar>
            
            <div className="text-left">
              <h2 className="font-serif text-sage-darker">{otherUser?.name}</h2>
              <p className="text-xs text-muted-foreground">Tap to view profile</p>
            </div>
          </button>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setProfileOpen(true)}
            className="text-sage-dark"
          >
            <User className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 px-6 py-4 overflow-y-auto" data-testid="chat-messages-area">
        <div className="max-w-2xl mx-auto space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-sage/10 flex items-center justify-center">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={otherUser?.picture} />
                  <AvatarFallback className="bg-sage/20 text-sage-dark text-2xl">
                    {otherUser?.name?.[0]}
                  </AvatarFallback>
                </Avatar>
              </div>
              <h3 className="text-lg font-serif text-sage-darker mb-1">
                You matched with {otherUser?.name}! 🎉
              </h3>
              <p className="text-muted-foreground text-sm">
                Start the conversation and get to know each other
              </p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwn = message.sender_id === user?.user_id;
              return (
                <div 
                  key={message.message_id} 
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex gap-2 max-w-[75%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                    {!isOwn && (
                      <Avatar className="w-8 h-8 flex-shrink-0">
                        <AvatarImage src={otherUser?.picture} />
                        <AvatarFallback className="bg-sage/10 text-sage-dark text-xs">
                          {otherUser?.name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div>
                      <div className={`px-4 py-2 rounded-2xl ${
                        isOwn 
                          ? 'bg-sage text-white rounded-br-md' 
                          : 'bg-white border border-sage/20 text-sage-darker rounded-bl-md'
                      }`}>
                        <p className="text-sm">{message.content}</p>
                      </div>
                      <p className={`text-xs text-muted-foreground mt-1 ${isOwn ? 'text-right' : ''}`}>
                        {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Message Input - Fixed at bottom */}
      <footer className="px-6 py-4 border-t border-sage/10 bg-white sticky bottom-0 z-10">
        <form onSubmit={handleSend} className="max-w-2xl mx-auto flex gap-2">
          <Input
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="flex-1 bg-sage/5 border-sage/20 rounded-full"
            autoFocus
            data-testid="chat-message-input"
          />
          <Button 
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-sage text-white hover:bg-sage-dark rounded-full px-4"
            data-testid="chat-send-button"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </footer>

      {/* Profile View Modal */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="bg-white border-sage/20 max-w-md max-h-[90vh] overflow-y-auto">
          {otherUser && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sage-darker font-serif text-center">
                  Profile
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Profile Header */}
                <div className="text-center">
                  <Avatar className="w-24 h-24 mx-auto border-4 border-sage/30">
                    <AvatarImage src={otherUser.picture} />
                    <AvatarFallback className="bg-sage/20 text-sage-dark text-3xl">
                      {otherUser.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <h2 className="text-2xl font-serif text-sage-darker mt-4">
                    {otherUser.name}
                  </h2>
                  {otherUser.location && (
                    <p className="text-muted-foreground flex items-center gap-1 justify-center">
                      <MapPin className="w-4 h-4" />
                      {otherUser.location}
                    </p>
                  )}
                </div>

                {/* Profile Details */}
                <div className="space-y-4">
                  {/* Phase */}
                  {otherUser.phase && (
                    <div className="flex items-center gap-3 p-3 bg-sage/5 rounded-xl">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        otherUser.phase === 'ttc' ? 'bg-clay/20 text-clay-dark' :
                        otherUser.phase === 'pregnant' ? 'bg-sage/20 text-sage-dark' : 'bg-purple-100 text-purple-600'
                      }`}>
                        {getPhaseIcon(otherUser.phase)}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Current Phase</p>
                        <p className="text-sage-darker font-medium">{getPhaseLabel(otherUser.phase)}</p>
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  {otherUser.location && (
                    <div className="flex items-center gap-3 p-3 bg-sage/5 rounded-xl">
                      <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-sage-dark" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="text-sage-darker font-medium">{otherUser.location}</p>
                      </div>
                    </div>
                  )}

                  {/* Bio */}
                  {otherUser.bio && (
                    <div className="p-4 bg-sage/5 rounded-xl">
                      <p className="text-xs text-muted-foreground mb-2">About</p>
                      <p className="text-sage-darker">{otherUser.bio}</p>
                    </div>
                  )}

                  {/* Interests */}
                  {otherUser.interests?.length > 0 && (
                    <div className="p-4 bg-sage/5 rounded-xl">
                      <p className="text-xs text-muted-foreground mb-2">Interests</p>
                      <div className="flex flex-wrap gap-2">
                        {otherUser.interests.map((interest, idx) => (
                          <Badge key={idx} variant="outline" className="border-sage/30 text-sage-dark">
                            {interest}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Close Button */}
                <Button
                  onClick={() => setProfileOpen(false)}
                  className="w-full bg-sage text-white hover:bg-sage-dark rounded-full"
                >
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrivateChat;
